import React, { useState } from 'react';
import {
  getViewportIcon,
  suggestNewViewport,
  getInheritingFrom,
  validateViewports,
  isWrongUrlType,
} from '../../utils/viewportUtils';
import { figmaFileName } from '../../utils/figmaPaste';
import useFigmaLinkRows from '../../utils/useFigmaLinkRows';
import FigmaLinkNote from './FigmaLinkNote';
import ShareGuideModal from './ShareGuideModal';
import AeroFigmaLinkNote from '../Aero/AeroFigmaLinkNote';
import AeroShareGuideModal from '../Aero/AeroShareGuideModal';
import './ViewportEditor.css';

/**
 * Reusable viewport editor: the per-breakpoint Figma link rows plus inline
 * validation, lifted verbatim from FormContent so Figmafolio and Aero share
 * one implementation. The component boundary is intentionally generic:
 *
 *   - viewports          the array of { name, minWidth, figmaUrl }
 *   - onViewportsChange   called with the next array on every edit
 *   - variant             'default' (Figmafolio) | 'aero' (re-skinned)
 *   - linkCheck           Figmafolio only. Turns design / file / branch links into
 *                         prototype links on paste, asks the server whether anyone can
 *                         open the prototype (one site at a time, from this page only),
 *                         and shows the result under the field. Never blocks saving.
 *
 * It owns no save/preview action: those are caller concerns. Editing rules are
 * unchanged: 2 to 6 viewports, a locked 0px base row, editable name and width,
 * empty links inherit from the next larger viewport, file/design links warn.
 */
export default function ViewportEditor({ viewports = [], onViewportsChange, variant = 'default', linkCheck = false }) {
  const [editingName, setEditingName] = useState(null);
  const [editingWidth, setEditingWidth] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Sort viewports by minWidth ascending for display
  const sortedViewports = [...viewports].sort((a, b) => a.minWidth - b.minWidth);

  // The Aero variant uses the aero-input field styling (loaded with the Aero
  // theme); Figmafolio keeps its form-input class. Everything else is shared.
  const urlInputBase = variant === 'aero' ? 'aero-input' : 'form-input';

  // ---- link check (both brands): one hook, see utils/useFigmaLinkRows.js ----
  const setRowUrl = (minWidth, newUrl) => {
    onViewportsChange(viewports.map((v) => (v.minWidth === minWidth ? { ...v, figmaUrl: newUrl || null } : v)));
  };
  const linkRows = useFigmaLinkRows({
    rows: viewports.map((v) => ({ id: v.minWidth, url: v.figmaUrl })),
    setUrl: setRowUrl,
    enabled: linkCheck,
  });
  const Note = variant === 'aero' ? AeroFigmaLinkNote : FigmaLinkNote;
  const Guide = variant === 'aero' ? AeroShareGuideModal : ShareGuideModal;

  const handleViewportUrlChange = (minWidth, newUrl) => {
    const updated = viewports.map((v) =>
      v.minWidth === minWidth ? { ...v, figmaUrl: newUrl || null } : v
    );
    onViewportsChange(updated);
  };

  const handleViewportNameChange = (minWidth, newName) => {
    const updated = viewports.map((v) =>
      v.minWidth === minWidth ? { ...v, name: newName } : v
    );
    onViewportsChange(updated);
  };

  const handleViewportWidthChange = (oldMinWidth, newMinWidth) => {
    const parsedWidth = parseInt(newMinWidth, 10);
    if (isNaN(parsedWidth) || parsedWidth < 0 || parsedWidth > 3840) return;

    // Check for duplicates
    if (viewports.some((v) => v.minWidth !== oldMinWidth && v.minWidth === parsedWidth)) return;

    const updated = viewports.map((v) =>
      v.minWidth === oldMinWidth ? { ...v, minWidth: parsedWidth } : v
    );
    onViewportsChange(updated);
  };

  const handleAddViewport = () => {
    if (viewports.length >= 6) return;

    const suggestion = suggestNewViewport(viewports);
    const newViewport = {
      name: suggestion.name,
      minWidth: suggestion.minWidth,
      figmaUrl: null,
    };
    onViewportsChange([...viewports, newViewport]);
  };

  const handleDeleteViewport = (minWidth) => {
    if (viewports.length <= 2) return;
    if (minWidth === 0) return; // Can't delete base viewport

    const updated = viewports.filter((v) => v.minWidth !== minWidth);
    onViewportsChange(updated);
    setDeleteConfirmId(null);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setEditingName(null);
    }
  };

  const handleWidthKeyDown = (e, oldMinWidth) => {
    if (e.key === 'Enter') {
      handleViewportWidthChange(oldMinWidth, e.target.value);
      setEditingWidth(null);
    } else if (e.key === 'Escape') {
      setEditingWidth(null);
    }
  };

  const handleWidthBlur = (e, oldMinWidth) => {
    handleViewportWidthChange(oldMinWidth, e.target.value);
    setEditingWidth(null);
  };

  // Check for validation errors
  const validationErrors = validateViewports(viewports);
  const hasUrlError = !linkCheck && viewports.some((v) => isWrongUrlType(v.figmaUrl));

  return (
    <div className={`viewport-editor${variant === 'aero' ? ' viewport-editor-aero' : ''}`}>
      <div className="viewport-list">
        {sortedViewports.map((viewport) => {
          const inheritingFrom = getInheritingFrom(viewport, viewports);
          const isBaseViewport = viewport.minWidth === 0;

          return (
            <div key={viewport.minWidth} className="viewport-row">
              <div className="viewport-info">
                <span className="viewport-icon">{getViewportIcon(viewport.minWidth)}</span>

                {editingName === viewport.minWidth ? (
                  <input
                    type="text"
                    className="viewport-name-input"
                    value={viewport.name}
                    onChange={(e) => handleViewportNameChange(viewport.minWidth, e.target.value)}
                    onKeyDown={(e) => handleNameKeyDown(e, viewport.minWidth)}
                    onBlur={() => setEditingName(null)}
                    autoFocus
                  />
                ) : (
                  <button
                    className="viewport-name-btn"
                    onClick={() => setEditingName(viewport.minWidth)}
                    type="button"
                  >
                    {viewport.name}
                  </button>
                )}

                {editingWidth === viewport.minWidth && !isBaseViewport ? (
                  <input
                    type="number"
                    className="viewport-width-input"
                    defaultValue={viewport.minWidth}
                    onKeyDown={(e) => handleWidthKeyDown(e, viewport.minWidth)}
                    onBlur={(e) => handleWidthBlur(e, viewport.minWidth)}
                    min="1"
                    max="3840"
                    autoFocus
                  />
                ) : (
                  <button
                    className={`viewport-width-btn ${isBaseViewport ? 'locked' : ''}`}
                    onClick={() => !isBaseViewport && setEditingWidth(viewport.minWidth)}
                    type="button"
                    disabled={isBaseViewport}
                    title={isBaseViewport ? 'Base viewport width cannot be changed' : 'Click to edit'}
                  >
                    {viewport.minWidth}px+
                    {!isBaseViewport && (
                      <svg className="viewport-width-pencil" width="1em" height="1em" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M11 2.5l2.5 2.5L5.5 13H3v-2.5L11 2.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
                      </svg>
                    )}
                  </button>
                )}
              </div>

              <div className="viewport-url-container">
                <input
                  className={`${urlInputBase} viewport-url-input ${!linkCheck && isWrongUrlType(viewport.figmaUrl) ? 'error' : ''}`}
                  type="text"
                  placeholder={inheritingFrom ? `Leave empty to use ${inheritingFrom}` : 'https://www.figma.com/proto/...'}
                  value={viewport.figmaUrl || ''}
                  onChange={(e) => handleViewportUrlChange(viewport.minWidth, e.target.value)}
                  onPaste={linkCheck ? (e) => linkRows.handlePaste(viewport.minWidth, e) : undefined}
                  onBlur={linkCheck ? (e) => linkRows.handleBlur(viewport.minWidth, e.target.value) : undefined}
                />
                {linkCheck && (
                  <Note
                    kind={linkRows.noteKind(viewport.minWidth, viewport.figmaUrl)}
                    onShowHow={() => linkRows.guide.open(viewport.minWidth)}
                    onRecheck={() => linkRows.recheck(viewport.minWidth, viewport.figmaUrl)}
                  />
                )}
                {!linkCheck && isWrongUrlType(viewport.figmaUrl) && (
                  <p className="figma-url-warning viewport-url-warning">
                    ⚠️ Use a prototype link (figma.com/proto/...)
                  </p>
                )}
              </div>

              <div className="viewport-actions">
                {viewports.length > 2 && !isBaseViewport && (
                  <>
                    {deleteConfirmId === viewport.minWidth ? (
                      <div className="viewport-delete-confirm">
                        <span>Delete?</span>
                        <button
                          className="viewport-delete-confirm-btn yes"
                          onClick={() => handleDeleteViewport(viewport.minWidth)}
                          type="button"
                        >
                          Yes
                        </button>
                        <button
                          className="viewport-delete-confirm-btn no"
                          onClick={() => setDeleteConfirmId(null)}
                          type="button"
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        className="viewport-delete-btn"
                        onClick={() => setDeleteConfirmId(viewport.minWidth)}
                        type="button"
                        title="Delete viewport"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}

        {viewports.length < 6 && (
          <button className="viewport-add-btn" onClick={handleAddViewport} type="button">
            <svg viewBox="0 0 16 16" fill="none" width="16" height="16">
              <path d="M8 1v14M1 8h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            Add Breakpoint
          </button>
        )}
      </div>

      {linkCheck && (
        <Guide
          show={linkRows.guide.openFor !== null}
          onClose={linkRows.guide.close}
          recheck={linkRows.guide.recheckQuiet}
          fileName={figmaFileName((viewports.find((v) => v.minWidth === linkRows.guide.openFor) || {}).figmaUrl)}
          onShared={() => {}}
        />
      )}

      {validationErrors.length > 0 && !hasUrlError && (
        <div className="viewport-validation-errors">
          {validationErrors.map((error, idx) => (
            <p key={idx} className="viewport-validation-error">⚠️ {error}</p>
          ))}
        </div>
      )}
    </div>
  );
}
